import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  StyledEngineProvider,
} from '@mui/material/styles';

import palette from './palette';
import shadows from './shadows';
import typography from './typography';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';
import { ThemeModeContext } from './ThemeModeContext';

// ----------------------------------------------------------------------

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const [mode, setMode] = useState('light');
  const location = useLocation();
  // determine whether the current path is part of the authenticated/dashboard area
  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  // load saved preference
  useEffect(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      setMode(savedMode);
    }
  }, []);

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  const themeOptions = useMemo(
    () => {
      // only apply dark/light toggle for authenticated/dashboard pages;
      // public routes are always light
      const appliedMode = isDashboardRoute ? mode : 'light';
      return {
        palette: palette(appliedMode),
        shape: { borderRadius: 6 },
        typography,
        shadows: shadows(appliedMode),
        customShadows: customShadows(appliedMode),
      };
    },
    [mode, isDashboardRoute]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </ThemeModeContext.Provider>
  );
}
